/**
 * Lacrosse Position Recommendation Rules
 * 
 * Defines position recommendations for Lacrosse players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Lacrosse Position Rules
 */
export const LACROSSE_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'G',
    tier: 'premium',
    description: 'Goalie - Requires positioning, reflexes, and communication',
    requirements: [
      { attribute: 'positioning', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const positioning = attrs.get('positioning') || 0;
      const awareness = attrs.get('awareness') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const focus = attrs.get('focus') || 0; // Mental attribute
      return positioning * 2.0 + awareness * 1.8 + agility * 1.4 + speed * 1.0 + focus * 1.2;
    },
  },
  {
    position: 'A',
    tier: 'premium',
    description: 'Attack - Requires shooting, passing, and offensive positioning',
    requirements: [
      { attribute: 'shooting', minValue: 7, required: true },
      { attribute: 'passing', minValue: 6, required: true },
      { attribute: 'catching', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const passing = attrs.get('passing') || 0;
      const catching = attrs.get('catching') || 0;
      const positioning = attrs.get('positioning') || 0;
      const agility = attrs.get('agility') || 0;
      const sport_iq = attrs.get('sport_iq') || 0;
      return shooting * 1.9 + passing * 1.6 + catching * 1.4 + positioning * 1.3 + agility * 1.1 + sport_iq * 1.1;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'M',
    tier: 'standard',
    description: 'Midfield - Requires all-around skills, ground balls, and stamina',
    requirements: [
      { attribute: 'ground_balls', minValue: 6, required: true },
      { attribute: 'passing', minValue: 6, required: true },
      { attribute: 'stamina', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const ground_balls = attrs.get('ground_balls') || 0;
      const passing = attrs.get('passing') || 0;
      const stamina = attrs.get('stamina') || 0;
      const shooting = attrs.get('shooting') || 0;
      const defense = attrs.get('defense') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      return ground_balls * 1.7 + passing * 1.5 + stamina * 1.4 + shooting * 1.2 + defense * 1.2 + (speed + agility) * 1.0;
    },
  },
  {
    position: 'D',
    tier: 'standard',
    description: 'Defense - Requires defensive positioning, checking, and communication',
    requirements: [
      { attribute: 'defense', minValue: 6, required: true },
      { attribute: 'positioning', minValue: 6, required: true },
      { attribute: 'ground_balls', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const defense = attrs.get('defense') || 0;
      const positioning = attrs.get('positioning') || 0;
      const ground_balls = attrs.get('ground_balls') || 0;
      const passing = attrs.get('passing') || 0;
      const awareness = attrs.get('awareness') || 0;
      const strength = attrs.get('strength') || 0;
      return defense * 1.8 + positioning * 1.6 + ground_balls * 1.3 + passing * 1.2 + awareness * 1.2 + strength * 1.1;
    },
  },
  {
    position: 'LSM',
    tier: 'standard',
    description: 'Long-stick Midfield - Requires ground balls, defense, and transition skills',
    requirements: [
      { attribute: 'ground_balls', minValue: 6, required: true },
      { attribute: 'defense', minValue: 6, required: true },
      { attribute: 'speed', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const ground_balls = attrs.get('ground_balls') || 0;
      const defense = attrs.get('defense') || 0;
      const speed = attrs.get('speed') || 0;
      const passing = attrs.get('passing') || 0;
      const stamina = attrs.get('stamina') || 0;
      const positioning = attrs.get('positioning') || 0;
      return ground_balls * 1.7 + defense * 1.6 + speed * 1.3 + passing * 1.2 + stamina * 1.2 + positioning * 1.1;
    },
  },

  // EASIER POSITIONS - Lower thresholds (accessible for less developed players)
  {
    position: 'F',
    tier: 'easier',
    description: 'FOGO (Face-Off Get-Off) - Specialized faceoff specialist',
    requirements: [
      { attribute: 'ground_balls', minValue: 5, required: true },
      { attribute: 'strength', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const ground_balls = attrs.get('ground_balls') || 0;
      const strength = attrs.get('strength') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const focus = attrs.get('focus') || 0;
      return ground_balls * 2.0 + strength * 1.4 + agility * 1.2 + speed * 1.1 + focus * 1.0;
    },
  },
];

